import React from "react";
import { type SxProps, TextField, type Theme } from "@mui/material";
import {
  type Control,
  Controller,
  type FieldValues,
  type Path,
} from "react-hook-form";

interface Props<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label: string;
  required?: boolean;
  disabled?: boolean;
  type?: "text" | "number";
  size?: "medium" | "small";
  multiline?: boolean;
  minRows?: number;
  helperText?: string;
  error?: boolean;
  onChange?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  sx?: SxProps<Theme>;
}

export const TextFieldWithControl = <T extends FieldValues>({
  control,
  name,
  label,
  required,
  disabled,
  type,
  size,
  multiline,
  minRows,
  helperText,
  error,
  onChange,
  sx,
}: Props<T>) => {
  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    if (type === "number") {
      e.currentTarget.blur();
    }
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        return (
          <TextField
            {...field}
            onChange={(e) => {
              field.onChange(e);
              if (onChange) onChange(e);
            }}
            required={required}
            fullWidth
            label={label}
            error={error || !!fieldState.error}
            helperText={fieldState.error?.message || helperText}
            size={size}
            sx={{ mb: 3, ...sx }}
            disabled={disabled}
            type={type}
            multiline={multiline}
            minRows={minRows}
            InputProps={{
              inputProps: {
                step: type === "number" ? "any" : undefined,
                onWheel: handleWheel,
              },
            }}
            InputLabelProps={{ shrink: field.value ? true : undefined }}
          />
        );
      }}
    />
  );
};
